/**
 * Restore & exports page — per docs/specs/restore.md
 *
 * Soft-delete recovery, GDPR data exports, snapshot status, split sheets.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-27-v4
 */
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { GitCompare, RotateCcw, Search, X } from 'lucide-react';
import { Chip } from '../components/chip';
import { useToast } from '../components/toast';
import {
    redoRestorableAction,
    retryGdprExport,
    undoRestorableAction,
    useGdprExports,
    useRestorableActions,
    useRestoreKpis,
    useRestoreTimeline,
    useSnapshots,
    useSoftDeletes,
    useSplitSheetStats
} from '../../lib/api';
import { getAuditDiffRows } from '../../lib/audit';
import type {
    AuditQueryFilters,
    GdprExportRow,
    RestorableAction,
    SnapshotRow
} from '../../lib/types';
import { RestoreHardDeleteModal } from './restore-hard-delete-modal';
import { RestoreSnapshotPurgeModal } from './restore-snapshot-purge-modal';
import { RoleButton } from '../components/role-gate';
import { useShell } from '../layout/shell';
import { DiffTable } from './audit-detail-panel';

const exportStatusTone = (s: GdprExportRow['status']) =>
    s === 'delivered'
        ? 'success'
        : s === 'ready'
          ? 'info'
          : s === 'building'
            ? 'warning'
            : 'danger';

const restoreComplexityLabel = (value: RestorableAction['restoration_complexity']) =>
    value === 'manual_required'
        ? 'manual required'
        : value === 'related_resources'
          ? 'related resources'
          : value.replace('_', ' ');

const restoreComplexityTone = (value: RestorableAction['restoration_complexity']) =>
    value === 'destructive' || value === 'manual_required'
        ? 'danger'
        : value === 'blocked'
          ? 'muted'
          : value === 'related_resources' || value === 'cascading'
            ? 'warning'
            : 'success';

const requiresRestoreConfirmation = (value: RestorableAction['restoration_complexity']) =>
    value === 'destructive' || value === 'cascading' || value === 'manual_required';

export function RestorePage() {
    const [params, setParams] = useSearchParams();
    const kpis = useRestoreKpis();
    const soft = useSoftDeletes();
    const timeline = useRestoreTimeline();
    const exports = useGdprExports();
    const snapshots = useSnapshots();
    const splitSheets = useSplitSheetStats();

    const [hardDeleteRow, setHardDeleteRow] = useState<GdprExportRow | null>(null);
    const [purgeSnapshot, setPurgeSnapshot] = useState<SnapshotRow | null>(null);
    const [actionSearch, setActionSearch] = useState('');
    const [actionResourceType, setActionResourceType] = useState('');
    const [actionComplexity, setActionComplexity] = useState<
        'all' | RestorableAction['restoration_complexity']
    >('all');
    const [selectedAction, setSelectedAction] = useState<RestorableAction | null>(null);
    const [restoreBusy, setRestoreBusy] = useState<string | null>(null);
    const qc = useQueryClient();
    const { setDetailPanel } = useShell();
    const { toast } = useToast();
    const [refreshing, setRefreshing] = useState(false);
    const actionFilters = useMemo<AuditQueryFilters>(
        () => ({
            q: actionSearch.trim() || undefined,
            source: 'action',
            resource_type: actionResourceType.trim() || undefined,
            restorable: true,
            limit: 100
        }),
        [actionResourceType, actionSearch]
    );
    const restorableActions = useRestorableActions(actionFilters);
    const visibleActions = (restorableActions.data ?? []).filter((action) =>
        actionComplexity === 'all' ? true : action.restoration_complexity === actionComplexity
    );

    useEffect(() => {
        const actionId = params.get('action');
        if (!actionId || selectedAction) return;
        const match = restorableActions.data?.find((action) => action.id === actionId);
        if (match) setSelectedAction(match);
    }, [params, restorableActions.data, selectedAction]);

    useEffect(() => {
        if (selectedAction) {
            setDetailPanel(
                <RestoreActionDetailPanel
                    action={selectedAction}
                    busy={restoreBusy === selectedAction.id}
                    onClose={() => {
                        setSelectedAction(null);
                        setDetailPanel(null);
                        const next = new URLSearchParams(params);
                        next.delete('action');
                        setParams(next, { replace: true });
                    }}
                    onUndo={() => void runRestoreAction(selectedAction, 'undo')}
                    onRedo={() => void runRestoreAction(selectedAction, 'redo')}
                />
            );
        } else {
            setDetailPanel(null);
        }

        return () => setDetailPanel(null);
        // runRestoreAction closes over state intentionally; the panel is rebuilt after each state change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, restoreBusy, selectedAction, setDetailPanel, setParams]);

    const hardDeleteEligibility = (row: GdprExportRow) => {
        if (row.type !== 'deletion') {
            return { enabled: false, reason: 'Only deletion requests can be hard-deleted.' };
        }

        if (row.status === 'building') {
            return {
                enabled: false,
                reason: 'Request is still building; hard-delete is blocked.'
            };
        }

        return { enabled: true, reason: '' };
    };

    const isCriticalStuck = (row: GdprExportRow) =>
        row.status === 'stuck' && (row.stuck_days || 0) >= 14;

    const refreshRestoreData = async () => {
        setRefreshing(true);
        try {
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'kpis'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'soft-deleted'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'timeline'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'restorable-actions'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'exports'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'snapshots'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'split-sheet-stats'] })
            ]);
            toast('Restore page refresh requested', {
                kind: 'info',
                detail: 'Latest rows requested from worker'
            });
        } catch {
            toast('Refresh failed', {
                kind: 'danger',
                detail: 'Unable to refresh restore page state right now'
            });
        } finally {
            setRefreshing(false);
        }
    };

    const handleRetry = async (row: GdprExportRow) => {
        try {
            await retryGdprExport(row.id);
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'exports'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'kpis'] })
            ]);
            toast('Export retry queued', { kind: 'info', detail: row.user_email });
        } catch (error) {
            toast('Retry failed', {
                kind: 'danger',
                detail: error instanceof Error ? error.message : 'Unable to requeue export'
            });
        }
    };

    function openAction(action: RestorableAction) {
        setSelectedAction(action);
        const next = new URLSearchParams(params);
        next.set('action', action.id);
        setParams(next, { replace: true });
    }

    async function runRestoreAction(action: RestorableAction, mode: 'undo' | 'redo') {
        if (restoreBusy) return;
        const verb = mode === 'undo' ? 'Undo' : 'Redo';
        const reason = window
            .prompt(
                `${verb} ${action.resource_type} ${action.resource_label ?? action.resource_id}. Reason required:`
            )
            ?.trim();
        if (!reason) {
            toast('Restore reason required', {
                kind: 'danger',
                detail: 'Undo and redo operations require explicit audit context.'
            });
            return;
        }

        if (
            requiresRestoreConfirmation(action.restoration_complexity) &&
            !window.confirm(
                `${verb} may have destructive side effects. Continue with ${action.related_resources?.length ?? 0} related resource(s)?`
            )
        ) {
            return;
        }

        setRestoreBusy(action.id);
        try {
            const result =
                mode === 'undo'
                    ? await undoRestorableAction(action.id, reason)
                    : await redoRestorableAction(action.id, reason);
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'restorable-actions'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'timeline'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'restore', 'kpis'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'admin-audit', 'audit'] })
            ]);
            setSelectedAction(result.action);
            toast(`${verb} complete`, {
                kind: 'success',
                detail: result.message ?? `${action.resource_type} ${action.resource_id}`
            });
        } catch (error) {
            toast(`${verb} failed`, {
                kind: 'danger',
                detail: error instanceof Error ? error.message : 'Restore operation failed'
            });
        } finally {
            setRestoreBusy(null);
        }
    }

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Restore & exports</h1>
                    <p>Soft-delete recovery, GDPR exports, split-sheets, snapshots.</p>
                </div>
                <div className="page-actions">
                    <RoleButton
                        scope="read"
                        className="btn btn-ghost"
                        onClick={() => void refreshRestoreData()}
                        disabled={refreshing}
                    >
                        {refreshing ? 'Refreshing…' : 'Refresh'}
                    </RoleButton>
                </div>
            </header>

            <section className="kpi-grid kpi-grid-6" aria-label="Restore metrics">
                <KpiMini
                    label="Soft-deleted"
                    value={kpis.data?.soft_deleted.toLocaleString() ?? '—'}
                />
                <KpiMini
                    label="Restores (30d)"
                    value={
                        kpis.data
                            ? `${kpis.data.restores_30d} (${kpis.data.self_service_pct.toFixed(0)}% self)`
                            : '—'
                    }
                />
                <KpiMini
                    label="Exports pending"
                    value={kpis.data?.exports_pending.toString() ?? '—'}
                    alert={!!kpis.data && kpis.data.exports_pending > 5}
                />
                <KpiMini
                    label="Split sheets (30d)"
                    value={kpis.data?.split_sheets_30d.toString() ?? '—'}
                />
                <KpiMini
                    label="Snapshot storage"
                    value={
                        kpis.data?.snapshots_available === false
                            ? 'unavailable'
                            : kpis.data
                              ? `${kpis.data.snapshot_storage_gb.toFixed(0)} GB`
                              : '—'
                    }
                />
                <KpiMini
                    label="Hard-delete queue"
                    value={kpis.data?.hard_delete_queue_24h.toString() ?? '—'}
                />
            </section>

            {kpis.data?.snapshots_available === false && (
                <section className="alert-bar">
                    <div className="alert" data-severity="warning">
                        <span className="msg">
                            Snapshot inventory is not persisted yet. The worker returns an explicit
                            unavailable state until the inventory store exists.
                        </span>
                        <span className="meta">source unavailable</span>
                    </div>
                </section>
            )}

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Soft-deleted by type</h2>
                    </header>
                    <div className="card-body tight">
                        {soft.data && soft.data.length > 0 ? (
                            soft.data.map((r) => (
                                <div
                                    key={r.type}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr auto',
                                        gap: 10,
                                        padding: '10px 16px',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12
                                    }}
                                >
                                    <span style={{ color: 'var(--text-primary)' }}>{r.type}</span>
                                    <span className="mono">{r.count.toLocaleString()}</span>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="title">
                                    No soft-deleted items in the current window
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Recent restores</h2>
                    </header>
                    <div className="card-body tight">
                        {timeline.data && timeline.data.length > 0 ? (
                            timeline.data.map((r) => (
                                <div
                                    key={r.id}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '60px 1fr auto',
                                        gap: 10,
                                        padding: '10px 16px',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12
                                    }}
                                >
                                    <span className="mono text-muted">{r.ts}</span>
                                    <div>
                                        <div style={{ color: 'var(--text-primary)' }}>
                                            {r.item_type}: {r.item_name}
                                        </div>
                                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                            by {r.actor}
                                        </div>
                                    </div>
                                    <Chip tone={r.reason === 'self_service' ? 'success' : 'info'}>
                                        {r.reason.replace('_', ' ')}
                                    </Chip>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state">
                                <div className="title">
                                    No restores recorded in the last 30 days
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">Restorable action history</h2>
                    <span className="text-muted" style={{ fontSize: 11 }}>
                        before/after snapshots · undo/redo guarded by confirmation
                    </span>
                </header>
                <div className="filter-bar" style={{ padding: '0 16px', margin: '0 0 8px' }}>
                    <label className="search">
                        <Search size={13} />
                        <input
                            type="search"
                            placeholder="Search actions, resources, reasons"
                            value={actionSearch}
                            onChange={(event) => setActionSearch(event.target.value)}
                        />
                    </label>
                    <input
                        className="select text-input"
                        type="text"
                        placeholder="resource type"
                        value={actionResourceType}
                        onChange={(event) => setActionResourceType(event.target.value)}
                    />
                    <select
                        className="select"
                        value={actionComplexity}
                        onChange={(event) =>
                            setActionComplexity(event.target.value as typeof actionComplexity)
                        }
                        aria-label="Restoration complexity"
                    >
                        <option value="all">Any complexity</option>
                        <option value="simple">Simple</option>
                        <option value="cascading">Cascading</option>
                        <option value="manual_required">Manual required</option>
                        <option value="related_resources">Related resources</option>
                        <option value="destructive">Destructive</option>
                        <option value="blocked">Blocked</option>
                    </select>
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => {
                            setActionSearch('');
                            setActionResourceType('');
                            setActionComplexity('all');
                        }}
                        disabled={
                            !actionSearch && !actionResourceType && actionComplexity === 'all'
                        }
                    >
                        Clear
                    </button>
                    <span className="count">{visibleActions.length} actions</span>
                </div>
                <div className="table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Resource</th>
                                <th>Action</th>
                                <th>Complexity</th>
                                <th>Actor</th>
                                <th>Status</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {restorableActions.isLoading ? (
                                <tr>
                                    <td colSpan={7} className="text-muted" style={{ padding: 16 }}>
                                        Loading restorable actions...
                                    </td>
                                </tr>
                            ) : restorableActions.error ? (
                                <tr>
                                    <td colSpan={7} style={{ padding: 16 }}>
                                        <div className="empty-state">
                                            <div className="title">
                                                Restorable actions unavailable
                                            </div>
                                            <div>
                                                {restorableActions.error instanceof Error
                                                    ? restorableActions.error.message
                                                    : 'The restore action endpoint returned an error.'}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ) : visibleActions.length ? (
                                visibleActions.map((action) => (
                                    <tr
                                        key={action.id}
                                        aria-selected={selectedAction?.id === action.id}
                                        onClick={() => openAction(action)}
                                    >
                                        <td className="mono text-muted">{action.ts}</td>
                                        <td>
                                            <div className="user-meta">
                                                <span className="user-name">
                                                    {action.resource_label ?? action.resource_id}
                                                </span>
                                                <span className="user-sub">
                                                    {action.resource_type} · {action.resource_id}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="mono">{action.action_type}</td>
                                        <td>
                                            <Chip
                                                tone={restoreComplexityTone(
                                                    action.restoration_complexity
                                                )}
                                            >
                                                {restoreComplexityLabel(
                                                    action.restoration_complexity
                                                )}
                                            </Chip>
                                        </td>
                                        <td>{action.actor}</td>
                                        <td>
                                            <Chip
                                                tone={
                                                    action.is_undone
                                                        ? 'warning'
                                                        : action.is_restorable
                                                          ? 'success'
                                                          : 'muted'
                                                }
                                                dot
                                            >
                                                {action.is_undone
                                                    ? 'undone'
                                                    : action.is_restorable
                                                      ? 'restorable'
                                                      : 'locked'}
                                            </Chip>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <RoleButton
                                                scope="support_ok"
                                                className="btn btn-sm btn-ghost"
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    void runRestoreAction(action, 'undo');
                                                }}
                                                disabled={
                                                    restoreBusy === action.id ||
                                                    !action.is_restorable ||
                                                    action.is_undone
                                                }
                                            >
                                                Undo
                                            </RoleButton>
                                            <RoleButton
                                                scope="support_ok"
                                                className="btn btn-sm btn-ghost"
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    void runRestoreAction(action, 'redo');
                                                }}
                                                disabled={
                                                    restoreBusy === action.id ||
                                                    !action.can_redo ||
                                                    !action.is_undone
                                                }
                                                style={{ marginLeft: 6 }}
                                            >
                                                Redo
                                            </RoleButton>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7}>
                                        <div className="empty-state" style={{ padding: '24px 0' }}>
                                            <div className="title">No restorable actions found</div>
                                            <div>
                                                Try clearing filters or checking action-log coverage
                                                for this resource type.
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="card">
                <header className="card-header">
                    <h2 className="card-title">GDPR export queue</h2>
                </header>
                <div className="table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Requested</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {exports.data && exports.data.length > 0 ? (
                                exports.data.map((e) => {
                                    const hardDelete = hardDeleteEligibility(e);
                                    const spacingStyle = hardDelete.enabled ? 6 : 0;
                                    return (
                                        <tr key={e.id}>
                                            <td>{e.user_email}</td>
                                            <td>
                                                <Chip
                                                    tone={e.type === 'deletion' ? 'danger' : 'info'}
                                                >
                                                    {e.type.replace('_', ' ')}
                                                </Chip>
                                            </td>
                                            <td>
                                                <Chip
                                                    tone={
                                                        isCriticalStuck(e)
                                                            ? 'danger'
                                                            : exportStatusTone(e.status)
                                                    }
                                                    dot
                                                >
                                                    {e.status}
                                                    {e.stuck_days && e.status === 'stuck' ? (
                                                        <span style={{ marginLeft: 4 }}>
                                                            ({e.stuck_days}d)
                                                        </span>
                                                    ) : null}
                                                </Chip>
                                            </td>
                                            <td className="text-muted">{e.requested_at}</td>
                                            <td style={{ textAlign: 'right' }}>
                                                {e.status === 'stuck' ? (
                                                    <button
                                                        className="btn btn-sm btn-ghost"
                                                        onClick={() => void handleRetry(e)}
                                                    >
                                                        Retry
                                                    </button>
                                                ) : null}
                                                {e.type === 'deletion' ? (
                                                    <RoleButton
                                                        scope="owner_only"
                                                        className="btn btn-sm btn-ghost"
                                                        onClick={() => setHardDeleteRow(e)}
                                                        disabled={!hardDelete.enabled}
                                                        title={
                                                            !hardDelete.enabled
                                                                ? hardDelete.reason
                                                                : undefined
                                                        }
                                                        style={{
                                                            marginLeft: spacingStyle,
                                                            color: hardDelete.enabled
                                                                ? 'var(--danger)'
                                                                : 'var(--text-muted)'
                                                        }}
                                                    >
                                                        Hard delete
                                                    </RoleButton>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={5}>
                                        <div className="empty-state" style={{ padding: '24px 0' }}>
                                            <div className="title">
                                                No GDPR exports in the queue
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <div className="grid-split">
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Snapshots</h2>
                        <span className="text-muted" style={{ fontSize: 11 }}>
                            90-day rolling · release R2 to reclaim space
                        </span>
                    </header>
                    <div className="table-wrap">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Id</th>
                                    <th>Created</th>
                                    <th className="numeric">Size</th>
                                    <th className="numeric">Users</th>
                                    <th className="numeric">Retention</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {snapshots.data && snapshots.data.length > 0 ? (
                                    snapshots.data.map((s) => (
                                        <tr key={s.id}>
                                            <td className="mono">{s.id}</td>
                                            <td className="text-muted">{s.created_at}</td>
                                            <td className="numeric mono">
                                                {s.size_gb.toFixed(1)} GB
                                            </td>
                                            <td className="numeric mono">
                                                {s.users_covered.toLocaleString()}
                                            </td>
                                            <td className="numeric text-muted">
                                                {s.retention_days}d
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <RoleButton
                                                    scope="owner_only"
                                                    className="btn btn-sm btn-ghost"
                                                    onClick={() => setPurgeSnapshot(s)}
                                                >
                                                    Purge
                                                </RoleButton>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6}>
                                            <div
                                                className="empty-state"
                                                style={{ padding: '24px 0' }}
                                            >
                                                <div className="title">
                                                    No snapshot inventory is persisted yet
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Split sheets · 30d</h2>
                    </header>
                    <div className="card-body">
                        {splitSheets.data && (
                            <dl className="kv-list">
                                <dt>Generated</dt>
                                <dd className="mono">{splitSheets.data.generated_30d}</dd>
                                <dt>Signed (DocuSign)</dt>
                                <dd className="mono">{splitSheets.data.signed_via_docusign}</dd>
                                <dt>Pending signature</dt>
                                <dd
                                    className="mono"
                                    style={{
                                        color:
                                            splitSheets.data.pending_signature > 0
                                                ? 'var(--warning)'
                                                : 'inherit'
                                    }}
                                >
                                    {splitSheets.data.pending_signature}
                                </dd>
                                <dt>Avg collaborators</dt>
                                <dd className="mono">
                                    {splitSheets.data.avg_collaborators.toFixed(1)}
                                </dd>
                                <dt>Most common dispute</dt>
                                <dd>{splitSheets.data.most_common_dispute}</dd>
                            </dl>
                        )}
                        {splitSheets.data?.telemetry_available === false && (
                            <div className="text-muted" style={{ fontSize: 11, marginTop: 8 }}>
                                Split-sheet telemetry is not persisted yet, so these values are
                                explicit zero-states rather than live metrics.
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <RestoreHardDeleteModal row={hardDeleteRow} onClose={() => setHardDeleteRow(null)} />
            <RestoreSnapshotPurgeModal
                snapshot={purgeSnapshot}
                onClose={() => setPurgeSnapshot(null)}
            />
        </div>
    );
}

function RestoreActionDetailPanel({
    action,
    busy,
    onClose,
    onUndo,
    onRedo
}: {
    action: RestorableAction;
    busy: boolean;
    onClose: () => void;
    onUndo: () => void;
    onRedo: () => void;
}) {
    const diffRows = getAuditDiffRows({
        state_before: action.state_before ?? null,
        state_after: action.state_after ?? null
    });

    return (
        <>
            <header className="detail-panel-header">
                <div className="audit-drawer-icon" data-source="action">
                    <GitCompare size={18} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <h2 className="name">{action.action_type}</h2>
                    <p className="email">
                        {action.resource_type} · {action.resource_label ?? action.resource_id}
                    </p>
                    <span className="uid">{action.id}</span>
                </div>
                <button className="close" onClick={onClose} aria-label="Close restore action">
                    <X size={16} />
                </button>
            </header>

            <div className="detail-panel-chips">
                <Chip
                    tone={
                        action.restoration_complexity === 'destructive'
                            ? 'danger'
                            : action.restoration_complexity === 'blocked'
                              ? 'muted'
                              : action.restoration_complexity === 'related_resources'
                                ? 'warning'
                                : 'success'
                    }
                >
                    {action.restoration_complexity.replace('_', ' ')}
                </Chip>
                <Chip tone={action.is_undone ? 'warning' : 'success'} dot>
                    {action.is_undone ? 'undone' : 'restorable'}
                </Chip>
            </div>

            <section className="detail-panel-section">
                <h3>Restore context</h3>
                <dl className="kv-list">
                    <dt>Occurred</dt>
                    <dd>{action.occurred_at ?? action.ts}</dd>
                    <dt>Actor</dt>
                    <dd>{action.actor}</dd>
                    <dt>Resource ID</dt>
                    <dd className="mono">{action.resource_id}</dd>
                    <dt>Reason</dt>
                    <dd>{action.reason ?? '—'}</dd>
                    <dt>Request ID</dt>
                    <dd className="mono">{action.request_id ?? '—'}</dd>
                    <dt>CF ray</dt>
                    <dd className="mono">{action.cf_ray ?? '—'}</dd>
                </dl>
            </section>

            <section className="detail-panel-section">
                <h3>Before / after</h3>
                <DiffTable rows={diffRows} />
            </section>

            {action.related_resources?.length ? (
                <section className="detail-panel-section">
                    <h3>Affected related resources</h3>
                    <div className="resource-history">
                        {action.related_resources.map((resource) => (
                            <article
                                className="resource-history-row"
                                key={`${resource.type}:${resource.id}`}
                            >
                                <div className="resource-history-title">
                                    {resource.label ?? resource.id}
                                </div>
                                <div className="text-muted">
                                    {resource.type} · {resource.id}
                                    {resource.effect ? ` · ${resource.effect}` : ''}
                                </div>
                            </article>
                        ))}
                    </div>
                </section>
            ) : null}

            <footer className="detail-panel-footer">
                <div className="row">
                    <RoleButton
                        scope="support_ok"
                        className="btn btn-primary"
                        type="button"
                        onClick={onUndo}
                        disabled={busy || !action.is_restorable || action.is_undone}
                    >
                        <RotateCcw size={13} /> {busy ? 'Running...' : 'Undo'}
                    </RoleButton>
                    <RoleButton
                        scope="support_ok"
                        className="btn btn-ghost"
                        type="button"
                        onClick={onRedo}
                        disabled={busy || !action.can_redo || !action.is_undone}
                    >
                        Redo
                    </RoleButton>
                </div>
            </footer>
        </>
    );
}

function KpiMini({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
    return (
        <div className="kpi-tile" data-alert={alert ? 'true' : 'false'}>
            <span className="label">{label}</span>
            <span className="value">{value}</span>
        </div>
    );
}
